import React from 'react';
import { Box, Typography, IconButton, Spec, Row, Col } from '@lucid/lego';
import type { OrderDetailsResponse } from '../../../../../../api/services/lupp/order/dto.order';
import { formatCurrency, formatDate, formatDateTime } from '../../../../../../utils/tools';
import { useStore } from '../../../../../../store/store';

type SummaryProps = {
  orderData: OrderDetailsResponse;
  theme: 'light' | 'dark';
  labels: Record<string, string>;
  locale: string;
  onDownloadAgreement: () => void;
  isAgreementAvailable: boolean;
  dataTestId?: string;
};

/** Shared Spec props for all summary fields */
const SPEC_PROPS = {
  variant: 'gray' as const,
  theme: 'light' as const,
  size: 'extraSmall' as const,
  type: 'sans' as const,
};

/**
 * Order Details Summary component
 * Displays key order information: dates, status, pricing, payment, and agreement download.
 *
 * @component
 * @param {SummaryProps} props - Component props
 * @returns {JSX.Element} Order details summary grid
 * @example
 * <Summary
 *   orderData={orderData}
 *   theme="dark"
 *   labels={labels}
 *   locale="en-US"
 *   onDownloadAgreement={handleDownload}
 * />
 */
const Summary: React.FC<SummaryProps> = ({
  orderData,
  theme,
  labels,
  locale,
  onDownloadAgreement,
  isAgreementAvailable,
  dataTestId = 'order-details-summary',
}) => {
  const language = useStore((state) => state.language) ?? undefined;
  return (
    <Box data-testid={dataTestId}>
      <Box mb={24}>
        <Typography variant="subtitle" size="small" theme={theme} color="black">
          {labels?.orderDetails || 'Order Details'}
        </Typography>
      </Box>
      <Box pt={24} pl={24} pb={24} pr={24} background={'gray-1'}>
        <Row gutter={[24, 0]}>
          <Col sm={12}>
            <Row gutter={[24, 0]} justify="space-between">
              <Col sm={6} lg={2}>
                <Spec
                  {...SPEC_PROPS}
                  eyebrow={labels?.orderDate || 'Order Date'}
                  title={formatDate(orderData.orderCreatedDate, locale, {}, language)}
                />
              </Col>
              <Col sm={6} lg={2}>
                <Spec
                  {...SPEC_PROPS}
                  eyebrow={labels?.orderStatus || 'Order Status'}
                  title={orderData.orderStatus || '-'}
                />
              </Col>
              <Col sm={6} lg={2}>
                <Spec
                  {...SPEC_PROPS}
                  eyebrow={labels?.totalPrice || 'Total Price'}
                  title={formatCurrency(orderData.orderTotal, locale)}
                />
              </Col>
              <Col sm={6} lg={2}>
                <Spec
                  {...SPEC_PROPS}
                  eyebrow={labels?.preferredPaymentMethod || 'Preferred Payment Method'}
                  title={orderData.preferredPaymentMethod || '-'}
                />
              </Col>
              <Col sm={6} lg={2}>
                <Spec
                  {...SPEC_PROPS}
                  eyebrow={labels?.financingStatus || 'Financing Status'}
                  title={orderData.financingStatus || '-'}
                />
              </Col>
            </Row>
          </Col>

          <Col sm={12}>
            <Row gutter={[24, 24]} justify="space-between">
              <Col sm={6} lg={2}>
                <Spec
                  {...SPEC_PROPS}
                  eyebrow={labels?.scheduledDeliveryDate || 'Scheduled Delivery Date'}
                  title={formatDateTime(orderData.scheduledDeliveryDate, locale, language)}
                />
              </Col>
              <Col sm={6} lg={2}>
                <Spec
                  {...SPEC_PROPS}
                  eyebrow={labels?.handoffDate || 'Handoff Date'}
                  title={formatDate(orderData.handOffDate, locale, {}, language)}
                />
              </Col>
              <Col sm={6} lg={2}>
                <Spec
                  {...SPEC_PROPS}
                  eyebrow={labels?.warrantyStartDate || 'Warranty Start Date'}
                  title={formatDate(orderData.warrantyStartDate, locale, {}, language)}
                />
              </Col>

              {/* Empty cell for alignment */}
              <Col sm={0} lg={2} />

              <Col sm={6} lg={2}>
                <Spec {...SPEC_PROPS} eyebrow={labels?.orderAgreement || 'Order Agreement'} />
                {isAgreementAvailable ? (
                  <IconButton
                    buttonType="tertiary"
                    color="neutral"
                    dataTestId="download-agreement-button"
                    gaComponent="IconButton"
                    handleClick={onDownloadAgreement}
                    icon={{ category: 'global', name: 'file' }}
                    size="small"
                    theme={theme === 'dark' ? 'light' : 'dark'}
                  />
                ) : (
                  <Spec {...SPEC_PROPS} title="-" />
                )}
              </Col>
            </Row>
          </Col>
        </Row>
      </Box>
    </Box>
  );
};

Summary.displayName = 'Summary';

export default Summary;
